import React from "react";

import { isAssignmentReassignable } from "./ReassignModal";
import { formatAssignedSince } from "./teamViewsHelpers";
import Badge from "../../../Components/Badge";
import Button from "../../../Components/Button";
import CommonModal from "../../../Components/Modal";
import type {
  TeamViewsAssignment,
  TeamViewsPerson,
} from "../../../Store/Reducers/TeamViewsSlice";

type AssignedAccountsModalProps = {
  open: boolean;
  person: TeamViewsPerson | null;
  onClose: () => void;
  /** Privileged viewers only (VA / C-Level / ESL / executiveOps). */
  canManageTeamCoverage?: boolean;
  onReassignAccount?: (
    person: TeamViewsPerson,
    assignment: TeamViewsAssignment
  ) => void;
};

/** Accounts list opened from a Cards click (prototype §7.1). */
const AssignedAccountsModal: React.FC<AssignedAccountsModalProps> = ({
  open,
  person,
  onClose,
  canManageTeamCoverage = false,
  onReassignAccount,
}) => {
  const name = person?.full_name || "Unknown";
  const assignments = person?.assignments || [];

  return (
    <CommonModal
      isOpen={open}
      toggle={onClose}
      size="2xl"
      usePortal
      title={`Assigned accounts — ${name}`}
      footer={
        <div className="flex justify-end">
          <Button variant="outline-primary" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      {assignments.length ? (
        <div className="max-h-[420px] overflow-y-auto">
          <table className="w-full table-fixed text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-left text-gray-500">
                <th
                  className={`px-3 py-2 font-medium ${
                    canManageTeamCoverage ? "w-[30%]" : "w-[40%]"
                  }`}
                >
                  Account
                </th>
                <th
                  className={`px-3 py-2 font-medium ${
                    canManageTeamCoverage ? "w-[24%]" : "w-[30%]"
                  }`}
                >
                  Role
                </th>
                <th
                  className={`px-3 py-2 font-medium ${
                    canManageTeamCoverage ? "w-[22%]" : "w-[30%]"
                  }`}
                >
                  Assigned since
                </th>
                {canManageTeamCoverage ? (
                  <th className="w-[24%] px-3 py-2 font-medium whitespace-nowrap">
                    Action
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {assignments.map(a => {
                const canReassign = isAssignmentReassignable(a);
                return (
                  <tr
                    key={a.account_id}
                    className="border-b border-gray-50 last:border-0"
                  >
                    <td className="truncate px-3 py-2 text-gray-900">
                      {a.account_name}
                    </td>
                    <td className="truncate px-3 py-2">{a.role_name || "—"}</td>
                    <td className="whitespace-nowrap px-3 py-2">
                      {formatAssignedSince(a.assigned_since)}
                    </td>
                    {canManageTeamCoverage ? (
                      <td className="whitespace-nowrap px-3 py-2">
                        {canReassign ? (
                          <button
                            type="button"
                            className="text-sm font-semibold text-purple-700 hover:text-purple-900 hover:underline"
                            onClick={() => {
                              if (!person) return;
                              onReassignAccount?.(person, a);
                            }}
                          >
                            Reassign / Remove
                          </button>
                        ) : (
                          <Badge label="Locked" type="long-assessment-purple" />
                        )}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-gray-500">No accounts assigned.</p>
      )}
    </CommonModal>
  );
};

export default AssignedAccountsModal;
